import "./Modals.scss";
import { Alert, Button, DatePicker, Form, Modal, Typography } from "antd";
import { useEffect, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { ModalProps, User } from "../../utils/types";
import { getErrorMessage, Notify } from "../../utils/constants";
import { terminateEmployment } from "../../utils/employmentService";

type TerminateEmploymentModalProps = ModalProps & {
  user: User;
  refreshData: () => Promise<void>;
};

const TerminateEmploymentModal = ({
  isOpen,
  setIsOpen,
  user,
  refreshData,
}: TerminateEmploymentModalProps) => {
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setEndDate(null);
      setError(null);
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!endDate) {
      Notify("error", "Termination date is required.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await terminateEmployment(user.id, endDate.format("YYYY-MM-DD"));
      Notify(
        "success",
        `Employment of ${user.firstName} ${user.lastName} ends on ${endDate.format("DD.MM.YYYY")}.`,
      );
      setIsOpen(false);
      await refreshData();
    } catch (err) {
      setError(
        getErrorMessage(err, "Oops! Something went wrong while terminating."),
      );
    } finally {
      setSaving(false);
    }
  };

  const startDate = user.employmentStartDate
    ? dayjs(user.employmentStartDate)
    : null;

  return (
    <Modal
      open={isOpen}
      title={`Terminate employment - ${user.firstName} ${user.lastName}`}
      onCancel={() => setIsOpen(false)}
      footer={[
        <Button key="cancel" onClick={() => setIsOpen(false)}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          danger
          loading={saving}
          onClick={handleSubmit}
        >
          Terminate
        </Button>,
      ]}
    >
      <Typography.Paragraph type="secondary">
        The employee keeps access until the end date (inclusive) and becomes
        inactive - without access to the application - from the next day.
        Leave days left at that date are only recorded on this employment;
        they are not carried into a future employment.
      </Typography.Paragraph>
      <Form layout="vertical" requiredMark>
        <Form.Item label="Last working day" required>
          <DatePicker
            format="DD.MM.YYYY"
            value={endDate}
            onChange={(value) => setEndDate(value)}
            disabledDate={(date) =>
              !!startDate && date.isBefore(startDate, "day")
            }
            style={{ width: "100%" }}
          />
        </Form.Item>
      </Form>
      {error && <Alert type="error" showIcon message={error} />}
    </Modal>
  );
};

export default TerminateEmploymentModal;
